/**
 * Lists and creates a patient's documents (by type) over the Phase 7 REST API.
 *
 * Backs the Documents tab subtabs: the Documents subtab loads every type
 * (no `type` filter passed) and filters client-side to editable library
 * documents (`markdown` / `blueprint`); the Sources subtab passes
 * `type="source"` so `GET /api/documents` filters server-side, cutting the
 * payload to just the ingested source documents instead of fetching every
 * document (including the far more numerous `entity` cards) and discarding
 * the rest client-side. New handles are created via POST /api/documents (no
 * version yet). Document titles are non-PHI metadata; nothing is logged.
 */
import { useCallback, useEffect, useState } from "react";
import apiUtils, { isApiError } from "../apiUtils";
import logger from "../utils/logger";
import { timeOperation } from "../utils/perfMetrics";
import type { BlueprintDocument } from "../types";

const DOCUMENTS_ENDPOINT = "/api/documents";

/** The lifecycle status of a library load. */
export type LibraryStatus = "loading" | "loaded" | "error";

/** The result surface returned by {@link useDocumentLibrary}. */
export interface UseDocumentLibraryResult {
  /** The current load status. */
  status: LibraryStatus;
  /** All of the patient's documents. */
  documents: BlueprintDocument[];
  /** Create a new document handle and refresh the list. */
  create: (title: string, type: string) => Promise<BlueprintDocument | null>;
  /** Rename a document (title-only) and refresh the list. */
  rename: (
    documentId: string,
    title: string,
  ) => Promise<BlueprintDocument | null>;
  /** Delete a document (and its versions/drafts); returns true on success. */
  remove: (documentId: string) => Promise<boolean>;
  /** Re-fetch the list. */
  reload: () => void;
}

/**
 * Load (and create) the documents belonging to a patient.
 *
 * @param patientId - The active patient id.
 * @param type - Optional document type filter (e.g. `"source"`), applied
 *   server-side. Omit to load every type (unchanged default behavior).
 * @returns The list status, documents, a create action, and a reload trigger.
 */
export function useDocumentLibrary(
  patientId: string,
  type?: string,
): UseDocumentLibraryResult {
  const [status, setStatus] = useState<LibraryStatus>("loading");
  const [documents, setDocuments] = useState<BlueprintDocument[]>([]);
  const [token, setToken] = useState(0);

  const reload = useCallback(() => setToken((t) => t + 1), []);

  useEffect(() => {
    let active = true;

    async function load(): Promise<void> {
      setStatus("loading");
      const typeParam = type ? `&type=${encodeURIComponent(type)}` : "";
      // BLA-348: "fetch sources" and "fetch document list" share this one
      // endpoint, distinguished only by the `type` query param — so the
      // client-timing operation name is picked from it, mirroring the
      // `doc_type` attribute the backend's own `documents.list` span carries
      // (see `app/routers/documents.py`) for the same request.
      const operation =
        type === "source" ? "sources.fetch_list" : "documents.fetch_list";
      const list = await timeOperation(
        operation,
        () =>
          apiUtils.fetchData<BlueprintDocument[]>(
            `${DOCUMENTS_ENDPOINT}?patient_id=${encodeURIComponent(patientId)}${typeParam}`,
          ),
        // `fetchData` RETURNS an ApiError rather than throwing, so
        // `timeOperation`'s own catch branch never sees an HTTP failure —
        // tag it here, or a 500 would be indistinguishable from a patient
        // with zero documents and its duration would land in the success
        // percentile.
        (result) => ({
          outcome: isApiError(result) ? "error" : "ok",
          count: isApiError(result) ? 0 : result.length,
        }),
      );
      if (!active) return;
      if (isApiError(list)) {
        setStatus("error");
        return;
      }
      setDocuments(list);
      setStatus("loaded");
    }

    load().catch((err: unknown) => {
      if (!active) return;
      logger.devOnly.warn("useDocumentLibrary: load failed", {
        error: String(err),
      });
      setStatus("error");
    });

    return () => {
      active = false;
    };
  }, [patientId, type, token]);

  const create = useCallback(
    async (title: string, type: string): Promise<BlueprintDocument | null> => {
      const resp = await apiUtils.fetchData<BlueprintDocument>(
        DOCUMENTS_ENDPOINT,
        "POST",
        { patient_id: patientId, title, type },
      );
      if (isApiError(resp)) {
        logger.devOnly.warn("useDocumentLibrary: create failed");
        return null;
      }
      reload();
      return resp;
    },
    [patientId, reload],
  );

  const rename = useCallback(
    async (
      documentId: string,
      title: string,
    ): Promise<BlueprintDocument | null> => {
      const resp = await apiUtils.fetchData<BlueprintDocument>(
        `${DOCUMENTS_ENDPOINT}/${encodeURIComponent(documentId)}`,
        "PATCH",
        { title },
      );
      if (isApiError(resp)) {
        logger.devOnly.warn("useDocumentLibrary: rename failed");
        return null;
      }
      reload();
      return resp;
    },
    [reload],
  );

  const remove = useCallback(
    async (documentId: string): Promise<boolean> => {
      const resp = await apiUtils.fetchData<Record<string, never>>(
        `${DOCUMENTS_ENDPOINT}/${encodeURIComponent(documentId)}`,
        "DELETE",
      );
      if (isApiError(resp)) {
        logger.devOnly.warn("useDocumentLibrary: delete failed");
        return false;
      }
      // Drop it locally for an immediate UI update, then reconcile with a reload.
      setDocuments((prev) => prev.filter((d) => d.id !== documentId));
      reload();
      return true;
    },
    [reload],
  );

  return { status, documents, create, rename, remove, reload };
}
